import React, { FC, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '~/design-system/components/Button/Button';
import {
  ButtonContentAlign,
  ButtonSize,
  ButtonVariant,
} from '~/design-system/components/Button/utils';
import { Icon } from '~/design-system/components/Icon/Icon';
import {
  DesignSystemIconSize,
  DesignSystemIconTone,
} from '~/design-system/components/Icon/utils';
import {
  SidebarItem,
  SidebarShell,
} from '~/design-system/components/Sidebar/Sidebar';
import { DesignSystemSidebarDensity } from '~/design-system/components/Sidebar/utils';
import { checkIsRouteMatch } from '~/helpers/routeMatcher';
import {
  JustdoneIcon,
  JustdoneLogo,
} from '~/projects/justdone/branding/JustdoneBrandLogo';

import { getHomePageV2SidebarNavigationEntries } from './homePageV2LayoutContent';
import { useHomePageV2SidebarActions } from './hooks/useHomePageV2SidebarActions';
import styles from './styles.module.scss';
import { HomePageV2SidebarProps } from './types';
import { HomePageV2AccountDropdown } from '../HomePageV2Header/HomePageV2AccountDropdown';

const HomePageV2Sidebar: FC<HomePageV2SidebarProps> = ({
  isMobile = false,
  onClose,
}) => {
  const { t } = useTranslation(['home', 'general']);
  const {
    activePath,
    navigateFromSidebar,
    navigateFromAccountMenu,
    handleUpgradeClick,
  } = useHomePageV2SidebarActions(onClose);
  const sidebarNavigationEntries = useMemo(
    () => getHomePageV2SidebarNavigationEntries(t),
    [t],
  );
  const homeSidebarEntry = sidebarNavigationEntries[0];

  return (
    <SidebarShell
      data-home-v2-shared-sidebar
      aria-label={
        isMobile
          ? t('v2.sidebar.aria.mobile_menu', 'Home navigation menu')
          : t('v2.sidebar.aria.desktop', 'Home navigation')
      }
      density={
        isMobile
          ? DesignSystemSidebarDensity.COMFORTABLE
          : DesignSystemSidebarDensity.COMPACT
      }
      headerActionLabel={t('v2.sidebar.go_to_home', 'Go to home')}
      onHeaderClick={(): void => navigateFromSidebar(homeSidebarEntry)}
      closeLabel={t('v2.sidebar.close_main_menu', 'Close main menu')}
      onClose={isMobile ? onClose : undefined}
      header={isMobile ? <JustdoneLogo variant="new" /> : <JustdoneIcon />}
      footer={
        isMobile ? (
          <div className={styles.mobile_sidebar_footer}>
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.MEDIUM}
              isFullWidth
              contentAlign={ButtonContentAlign.START}
              onClick={handleUpgradeClick}
              StartIcon={() => (
                <Icon
                  name="general/upgrade/24"
                  size={DesignSystemIconSize.LARGE}
                  tone={DesignSystemIconTone.ON_COLOR}
                />
              )}
            >
              {t('general:upgrade', 'Upgrade')}
            </Button>
          </div>
        ) : (
          <div className={styles.desktop_account_footer}>
            <HomePageV2AccountDropdown
              align="end"
              languageDropdownSide="right"
              side="right"
              sideOffset={12}
              triggerId="layout-desktop-sidebar"
              onNavigate={navigateFromAccountMenu}
            />
          </div>
        )
      }
    >
      {sidebarNavigationEntries.map((entry) => (
        <SidebarItem
          key={entry.sideMenuButton}
          icon={entry.icon}
          label={entry.label}
          isActive={checkIsRouteMatch(entry.path, activePath)}
          isCollapsed={!isMobile}
          onClick={(): void => navigateFromSidebar(entry)}
        />
      ))}
    </SidebarShell>
  );
};

export default HomePageV2Sidebar;
